import React, { useEffect, useState } from "react";
import { Bot, Send, AlertTriangle, Trash2 } from "lucide-react";
import {
  PostAgentChatStream,
  PostAgentConfirmChat,
  PostAgentRejectChat,
  PostAgentSimpleChat
} from "./apis/post";

type Role = "user" | "assistant";
const CHAT_STORAGE_KEY = "ai_agent_chat_messages_v1";
const DEFAULT_ASSISTANT_MESSAGE = "Hello. Ask me about your database or MCP tools.";
const LOADING_MESSAGES = [
  "Analyzing your request",
  "Checking database context",
  "Preparing the best response",
  "Reviewing available tools",
  "Collecting relevant details",
  "Drafting a clear answer",
  "Validating query approach",
  "Organizing the result",
  "Almost there, finalizing",
  "Working through your request",
];

interface ChatMsg {
  role: Role;
  content: string;
}

interface PendingConfirmation {
  tool_name?: string;
  sql?: string;
  token?: string;
  params?: any;
  message?: string;
  is_manual?: boolean;
  risk_level?: string;
  expires_in_seconds?: number;
}

const CONFIRM_WORDS = ["yes", "y", "confirm", "ok", "okay", "proceed", "run", "execute"];
const CANCEL_WORDS = ["no", "n", "cancel", "stop"];

const extractTokenFromText = (text: string) => {
  const match = text.match(/confirm\s*=\s*"([^"]+)"/i);
  return match?.[1] || null;
};

const extractExpiryFromText = (text: string) => {
  const match = text.match(/expires\s+in\s+(\d+)s/i);
  return match ? Number(match[1]) : undefined;
};

const getRiskUi = (riskLevel?: string) => {
  const risk = (riskLevel || "medium").toLowerCase();
  if (risk === "high") {
    return {
      panel: "border-red-500/40 bg-red-500/10",
      title: "text-red-200",
      badge: "bg-red-500/20 border border-red-500/40 text-red-200",
    };
  }
  if (risk === "low") {
    return {
      panel: "border-emerald-500/30 bg-emerald-500/10",
      title: "text-emerald-200",
      badge: "bg-emerald-500/20 border border-emerald-500/40 text-emerald-200",
    };
  }
  return {
    panel: "border-amber-500/30 bg-amber-500/10",
    title: "text-amber-200",
    badge: "bg-amber-500/20 border border-amber-500/40 text-amber-200",
  };
};

export default function AIAgentChat() {
  const [messages, setMessages] = useState<ChatMsg[]>(() => {
    try {
      const raw = localStorage.getItem(CHAT_STORAGE_KEY);
      if (!raw) {
        return [{ role: "assistant", content: DEFAULT_ASSISTANT_MESSAGE }];
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch {
      // ignore storage parse errors
    }
    return [{ role: "assistant", content: DEFAULT_ASSISTANT_MESSAGE }];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const streamMode = false;

  const appendMessage = (role: Role, content: string) => {
    setMessages(prev => [...prev, { role, content }]);
  };

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // ignore storage write errors
    }
  }, [messages]);

  useEffect(() => {
    if (!loading) return;

    const pickRandom = () =>
      LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];

    const timer = window.setInterval(() => {
      setLoadingMessage(pickRandom());
    }, 2000);

    return () => {
      window.clearInterval(timer);
    };
  }, [loading]);

  const clearChat = () => {
    setMessages([{ role: "assistant", content: DEFAULT_ASSISTANT_MESSAGE }]);
    setPendingConfirmation(null);
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
    } catch {
      // ignore storage remove errors
    }
  };

  const renderInline = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const renderAssistantContent = (content: string) => {
    const lines = content.split("\n");
    const blocks: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const numbered = line.match(/^(\d+)\.\s+(.*)$/);
      const bulleted = line.match(/^-\s+(.*)$/);

      if (numbered) {
        const items: string[] = [];
        let start = Number(numbered[1]) || 1;
        while (i < lines.length) {
          const m = lines[i].match(/^(\d+)\.\s+(.*)$/);
          if (!m) break;
          if (items.length === 0) {
            start = Number(m[1]) || 1;
          }
          items.push(m[2]);
          i += 1;
        }
        blocks.push(
          <ol key={`ol-${i}`} start={start} className="list-decimal pl-5 space-y-1">
            {items.map((item, idx) => (
              <li key={idx}>{renderInline(item)}</li>
            ))}
          </ol>
        );
        continue;
      }

      if (bulleted) {
        const items: string[] = [];
        while (i < lines.length) {
          const m = lines[i].match(/^-\s+(.*)$/);
          if (!m) break;
          items.push(m[1]);
          i += 1;
        }
        blocks.push(
          <ul key={`ul-${i}`} className="list-disc pl-5 space-y-1">
            {items.map((item, idx) => (
              <li key={idx}>{renderInline(item)}</li>
            ))}
          </ul>
        );
        continue;
      }

      if (!line.trim()) {
        blocks.push(<div key={`sp-${i}`} className="h-2" />);
      } else {
        blocks.push(
          <p key={`p-${i}`} className="leading-relaxed">
            {renderInline(line)}
          </p>
        );
      }
      i += 1;
    }

    return <div className="space-y-1">{blocks}</div>;
  };

  const sendMessage = async (text: string) => {
    setLoading(true);
    setLoadingMessage(
      LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]
    );
    appendMessage("user", text);

    const nextHistory = [...messages, { role: "user", content: text }];
    try {
      let data: any = null;
      let hasRenderedFromStream = false;

      if (streamMode) {
        try {
          // data = await PostAgentChatStream(
          //   { messages: nextHistory, stream: true },
          //   {
          //     onResult: (payload: any) => {
          //       data = payload;
          //       if (!hasRenderedFromStream && payload?.response) {
          //         appendMessage("assistant", payload.response);
          //         hasRenderedFromStream = true;
          //       }
          //     },
          //     onError: (error: string) => {
          //       throw new Error(error || "Stream failed");
          //     }
          //   }
          // );
          data = await PostAgentSimpleChat({ messages: nextHistory, stream: false });
        } catch (streamErr: any) {
          // Fallback to non-stream endpoint to avoid blocking user on parser/proxy edge cases.
          data = await PostAgentSimpleChat({ messages: nextHistory, stream: false });
          if (!data || typeof data !== "object") {
            throw streamErr;
          }
        }
      } else {
        data = await PostAgentSimpleChat({ messages: nextHistory, stream: false });
      }

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response");
      }
      if ((data as any).error) {
        throw new Error((data as any).error);
      }
      const reply = data.response || "[No response]";
      if (!hasRenderedFromStream) {
        appendMessage("assistant", reply);
      }

      const requestedByFlags = !!data?.requires_confirmation;

      if (requestedByFlags && data.confirmation) {
        const tokenFromText = extractTokenFromText(reply);
        const riskLevel = data?.confirmation?.risk_level || data?.risk_level;
        const expires = extractExpiryFromText(data?.confirmation?.message || reply);
        setPendingConfirmation({
          ...data.confirmation,
          token: data.confirmation.token || tokenFromText || undefined,
          risk_level: riskLevel,
          expires_in_seconds: expires,
          is_manual: false,
        });
      } else if (requestedByFlags) {
        const tokenFromText = extractTokenFromText(reply);
        const riskLevel = data?.risk_level || data?.confirmation?.risk_level || "high";
        const expires = extractExpiryFromText(reply);
        setPendingConfirmation({
          message:
            "Confirmation UI opened, but backend did not return confirmation payload (tool_name/sql/token). " +
            "Run cannot be executed until backend sends those fields.",
          sql: data?.confirmation?.sql || "",
          tool_name: data?.confirmation?.tool_name,
          token: data?.confirmation?.token || tokenFromText || undefined,
          params: data?.confirmation?.params || null,
          risk_level: riskLevel,
          expires_in_seconds: expires,
          is_manual: true,
        });
      } else {
        setPendingConfirmation(null);
      }
    } catch (err: any) {
      appendMessage("assistant", `Error: ${err?.message || "Request failed"}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmPending = async () => {
    if (!pendingConfirmation) return;
    if (
      pendingConfirmation.is_manual ||
      !pendingConfirmation.tool_name ||
      !pendingConfirmation.sql ||
      !pendingConfirmation.token
    ) {
      appendMessage(
        "assistant",
        "Run blocked: backend did not provide confirmation token/sql/tool_name. Please retry request or fix backend response to include `requires_confirmation=true` with `confirmation` object."
      );
      setPendingConfirmation(null);
      return;
    }
    setLoading(true);
    try {
      const data = await PostAgentConfirmChat({
        tool_name: pendingConfirmation.tool_name,
        sql: pendingConfirmation.sql,
        token: pendingConfirmation.token,
        params: Array.isArray(pendingConfirmation.params) ? pendingConfirmation.params : null
      });
      if (!data || typeof data !== "object") {
        throw new Error("Invalid response");
      }
      if ((data as any).error) {
        throw new Error((data as any).error);
      }
      appendMessage("assistant", data.response || "Confirmed.");
      setPendingConfirmation(null);
    } catch (err: any) {
      appendMessage("assistant", `Confirmation error: ${err?.message || "Request failed"}`);
    } finally {
      setLoading(false);
    }
  };

  const cancelPending = async () => {
    if (!pendingConfirmation) return;
    try {
      const data = await PostAgentRejectChat({
        tool_name: pendingConfirmation.tool_name || null,
        sql: pendingConfirmation.sql || null,
        token: pendingConfirmation.token || null,
        reason: "Canceled from UI",
      });
      const reply = data?.response || "Confirmation canceled. No change was executed.";
      appendMessage("assistant", reply);
    } catch {
      appendMessage("assistant", "Confirmation canceled. No change was executed.");
    }
    setPendingConfirmation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    if (pendingConfirmation) {
      const v = text.toLowerCase();
      appendMessage("user", text);
      if (CONFIRM_WORDS.includes(v)) {
        await confirmPending();
        return;
      }
      if (CANCEL_WORDS.includes(v)) {
        await cancelPending();
        return;
      }
      appendMessage("assistant", 'There is a pending confirmation. Click Confirm or type "confirm" / "cancel".');
      return;
    }

    await sendMessage(text);
  };

  return (
    <div className="h-[calc(100vh-6rem)] p-4">
      <div className="h-full rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden grid grid-rows-[auto_1fr_auto_auto]">
        <header className="px-5 py-4 border-b border-slate-800 bg-slate-900/70">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-white font-black tracking-wide flex items-center gap-2">
              <Bot size={18} className="text-cyan-300" />
              MCP AI Agent Chat
            </h1>
            <button
              type="button"
              onClick={clearChat}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
            >
              <Trash2 size={14} />
              Clear Chat
            </button>
          </div>
        </header>

        <section className="p-4 overflow-y-auto space-y-3">
          {messages.map((m, i) => (
            <div
              key={`${m.role}-${i}`}
              className={`max-w-[85%] rounded-xl border px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user"
                  ? "ml-auto bg-blue-900/30 border-blue-500/30 text-blue-100"
                  : "mr-auto bg-slate-900 border-slate-700 text-slate-100"
                }`}
            >
              <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">
                {m.role === "user" ? "You" : "Agent"}
              </div>
              {m.role === "assistant" ? renderAssistantContent(m.content) : m.content}
            </div>
          ))}
          {loading && (
            <div className="max-w-[85%] mr-auto bg-slate-900 border border-slate-700 text-slate-100 rounded-xl px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">Agent</div>
              <div className="inline-flex items-center gap-2 text-slate-300">
                <span>{loadingMessage}</span>
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce [animation-delay:-0.2s]"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce [animation-delay:-0.1s]"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce"></span>
                </span>
              </div>
            </div>
          )}
        </section>

        {pendingConfirmation && (
          <section className={`border-t p-3 grid gap-2 ${getRiskUi(pendingConfirmation.risk_level).panel}`}>
            <div className={`text-sm font-bold flex items-center gap-2 ${getRiskUi(pendingConfirmation.risk_level).title}`}>
              <AlertTriangle size={16} />
              Confirmation Required
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${getRiskUi(pendingConfirmation.risk_level).badge}`}>
                {(pendingConfirmation.risk_level || "medium").toUpperCase()} Risk
              </span>
            </div>
            {pendingConfirmation.message && (
              <div className="text-xs text-slate-100 bg-slate-900/70 border border-slate-700 rounded-lg p-2 whitespace-pre-wrap">
                {pendingConfirmation.message}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="text-[11px] text-slate-300 bg-slate-900/70 border border-slate-700 rounded-lg p-2">
                <div className="uppercase tracking-wider text-slate-400 mb-1">Tool</div>
                <div className="font-mono">{pendingConfirmation.tool_name || "N/A"}</div>
              </div>
              <div className="text-[11px] text-slate-300 bg-slate-900/70 border border-slate-700 rounded-lg p-2">
                <div className="uppercase tracking-wider text-slate-400 mb-1">Token</div>
                <div className="font-mono break-all">{pendingConfirmation.token || "N/A"}</div>
                {pendingConfirmation.expires_in_seconds ? (
                  <div className="text-[10px] text-slate-400 mt-1">
                    Expires in {pendingConfirmation.expires_in_seconds}s
                  </div>
                ) : null}
              </div>
            </div>
            <pre className="text-xs text-slate-100 bg-slate-900 border border-slate-700 rounded-lg p-2 overflow-auto max-h-40 whitespace-pre-wrap">
              {pendingConfirmation.sql || "[No SQL provided]"}
            </pre>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={confirmPending}
                disabled={loading}
                className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider"
              >
                Run
              </button>
              <button
                type="button"
                onClick={cancelPending}
                disabled={loading}
                className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider"
              >
                Reject
              </button>
            </div>
          </section>
        )}

        <form onSubmit={handleSubmit} className="p-3 border-t border-slate-800 grid grid-cols-[1fr_auto] gap-2 bg-slate-900/60">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
              }
            }}
            placeholder="Ask something..."
            className="min-h-[56px] max-h-[180px] resize-y rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="self-end h-[42px] px-4 rounded-xl bg-cyan-700 hover:bg-cyan-600 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-70"
          >
            <Send size={14} />
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
